import { __extends } from "tslib";
import { SceneLoader } from "../Loading/sceneLoader";
import { Tools } from "./tools";
import { Observable } from "./observable";
import { Texture } from "../Materials/Textures/texture";
import { CubeTexture } from "../Materials/Textures/cubeTexture";
import { HDRCubeTexture } from "../Materials/Textures/hdrCubeTexture";
import { EquiRectangularCubeTexture } from "../Materials/Textures/equiRectangularCubeTexture";
import { Logger } from "../Misc/logger";
/**
 * Defines the list of states available for a task inside a AssetsManager
 */
export var AssetTaskState;
(function (AssetTaskState) {
    /**
     * Initialization
     */
    AssetTaskState[AssetTaskState["INIT"] = 0] = "INIT";
    /**
     * Running
     */
    AssetTaskState[AssetTaskState["RUNNING"] = 1] = "RUNNING";
    /**
     * Done
     */
    AssetTaskState[AssetTaskState["DONE"] = 2] = "DONE";
    /**
     * Error
     */
    AssetTaskState[AssetTaskState["ERROR"] = 3] = "ERROR";
})(AssetTaskState || (AssetTaskState = {}));
/**
 * Define an abstract asset task used with a AssetsManager class to load assets into a scene
 */
var AbstractAssetTask = /** @class */ (function () {
    /**
     * Creates a new AssetsManager
     * @param name defines the name of the task
     */
    function AbstractAssetTask(
    /**
     * Task name
     */ name) {
        this.name = name;
        this._isCompleted = false;
        this._taskState = AssetTaskState.INIT;
    }
    Object.defineProperty(AbstractAssetTask.prototype, "isCompleted", {
        /**
         * Get if the task is completed
         */
        get: function () {
            return this._isCompleted;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractAssetTask.prototype, "taskState", {
        /**
         * Gets the current state of the task
         */
        get: function () {
            return this._taskState;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractAssetTask.prototype, "errorObject", {
        /**
         * Gets the current error object (if task is in error)
         */
        get: function () {
            return this._errorObject;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Internal only
     * @hidden
     */
    AbstractAssetTask.prototype._setErrorObject = function (message, exception) {
        if (this._errorObject) {
            return;
        }
        this._errorObject = {
            message: message,
            exception: exception
        };
    };
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    AbstractAssetTask.prototype.run = function (scene, onSuccess, onError) {
        var _this = this;
        this._taskState = AssetTaskState.RUNNING;
        this.runTask(scene, function () {
            _this.onDoneCallback(onSuccess, onError);
        }, function (msg, exception) {
            _this.onErrorCallback(onError, msg, exception);
        });
    };
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    AbstractAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        throw new Error("runTask is not implemented");
    };
    /**
     * Reset will set the task state back to INIT, so the next load call of the assets manager will execute this task again.
     * This can be used with failed tasks that have the reason for failure fixed.
     */
    AbstractAssetTask.prototype.reset = function () {
        this._taskState = AssetTaskState.INIT;
    };
    AbstractAssetTask.prototype.onErrorCallback = function (onError, message, exception) {
        this._taskState = AssetTaskState.ERROR;
        this._errorObject = {
            message: message,
            exception: exception
        };
        if (this.onError) {
            this.onError(this, message, exception);
        }
        onError();
    };
    AbstractAssetTask.prototype.onDoneCallback = function (onSuccess, onError) {
        try {
            this._taskState = AssetTaskState.DONE;
            this._isCompleted = true;
            if (this.onSuccess) {
                this.onSuccess(this);
            }
            onSuccess();
        }
        catch (e) {
            this.onErrorCallback(onError, "Task is done, error executing success callback(s)", e);
        }
    };
    return AbstractAssetTask;
}());
export { AbstractAssetTask };
/**
 * Class used to share progress information about assets loading
 */
var AssetsProgressEvent = /** @class */ (function () {
    /**
     * Creates a AssetsProgressEvent
     * @param remainingCount defines the number of remaining tasks to process
     * @param totalCount defines the total number of tasks
     * @param task defines the task that was just processed
     */
    function AssetsProgressEvent(remainingCount, totalCount, task) {
        this.remainingCount = remainingCount;
        this.totalCount = totalCount;
        this.task = task;
    }
    return AssetsProgressEvent;
}());
export { AssetsProgressEvent };
/**
 * Define a task used by AssetsManager to load assets into a container
 */
var ContainerAssetTask = /** @class */ (function (_super) {
    __extends(ContainerAssetTask, _super);
    /**
     * Creates a new ContainerAssetTask
     * @param name defines the name of the task
     * @param meshesNames defines the list of mesh's names you want to load
     * @param rootUrl defines the root url to use as a base to load your meshes and associated resources
     * @param sceneFilename defines the filename or File of the scene to load from
     */
    function ContainerAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the list of mesh's names you want to load
     */
    meshesNames, 
    /**
     * Defines the root url to use as a base to load your meshes and associated resources
     */
    rootUrl, 
    /**
     * Defines the filename or File of the scene to load from
     */
    sceneFilename) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.meshesNames = meshesNames;
        _this.rootUrl = rootUrl;
        _this.sceneFilename = sceneFilename;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    ContainerAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var _this = this;
        SceneLoader.LoadAssetContainer(this.rootUrl, this.sceneFilename, scene, function (container) {
            _this.loadedContainer = container;
            _this.loadedMeshes = container.meshes;
            _this.loadedParticleSystems = container.particleSystems;
            _this.loadedSkeletons = container.skeletons;
            _this.loadedAnimationGroups = container.animationGroups;
            onSuccess();
        }, null, function (scene, message, exception) {
            onError(message, exception);
        });
    };
    return ContainerAssetTask;
}(AbstractAssetTask));
export { ContainerAssetTask };
/**
 * Define a task used by AssetsManager to load meshes
 */
var MeshAssetTask = /** @class */ (function (_super) {
    __extends(MeshAssetTask, _super);
    /**
     * Creates a new MeshAssetTask
     * @param name defines the name of the task
     * @param meshesNames defines the list of mesh's names you want to load
     * @param rootUrl defines the root url to use as a base to load your meshes and associated resources
     * @param sceneFilename defines the filename or File of the scene to load from
     */
    function MeshAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the list of mesh's names you want to load
     */
    meshesNames, 
    /**
     * Defines the root url to use as a base to load your meshes and associated resources
     */
    rootUrl, 
    /**
     * Defines the filename or File of the scene to load from
     */
    sceneFilename) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.meshesNames = meshesNames;
        _this.rootUrl = rootUrl;
        _this.sceneFilename = sceneFilename;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    MeshAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var _this = this;
        SceneLoader.ImportMesh(this.meshesNames, this.rootUrl, this.sceneFilename, scene, function (meshes, particleSystems, skeletons, animationGroups) {
            _this.loadedMeshes = meshes;
            _this.loadedParticleSystems = particleSystems;
            _this.loadedSkeletons = skeletons;
            _this.loadedAnimationGroups = animationGroups;
            onSuccess();
        }, null, function (scene, message, exception) {
            onError(message, exception);
        });
    };
    return MeshAssetTask;
}(AbstractAssetTask));
export { MeshAssetTask };
/**
 * Define a task used by AssetsManager to load text content
 */
var TextFileAssetTask = /** @class */ (function (_super) {
    __extends(TextFileAssetTask, _super);
    /**
     * Creates a new TextFileAssetTask object
     * @param name defines the name of the task
     * @param url defines the location of the file to load
     */
    function TextFileAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the file to load
     */
    url) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    TextFileAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var _this = this;
        scene._loadFile(this.url, function (data) {
            _this.text = data;
            onSuccess();
        }, undefined, false, false, function (request, exception) {
            if (request) {
                onError(request.status + " " + request.statusText, exception);
            }
        });
    };
    return TextFileAssetTask;
}(AbstractAssetTask));
export { TextFileAssetTask };
/**
 * Define a task used by AssetsManager to load binary data
 */
var BinaryFileAssetTask = /** @class */ (function (_super) {
    __extends(BinaryFileAssetTask, _super);
    /**
     * Creates a new BinaryFileAssetTask object
     * @param name defines the name of the new task
     * @param url defines the location of the file to load
     */
    function BinaryFileAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the file to load
     */
    url) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    BinaryFileAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var _this = this;
        scene._loadFile(this.url, function (data) {
            _this.data = data;
            onSuccess();
        }, undefined, true, true, function (request, exception) {
            if (request) {
                onError(request.status + " " + request.statusText, exception);
            }
        });
    };
    return BinaryFileAssetTask;
}(AbstractAssetTask));
export { BinaryFileAssetTask };
/**
 * Define a task used by AssetsManager to load images
 */
var ImageAssetTask = /** @class */ (function (_super) {
    __extends(ImageAssetTask, _super);
    /**
     * Creates a new ImageAssetTask
     * @param name defines the name of the task
     * @param url defines the location of the image to load
     */
    function ImageAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the image to load
     */
    url) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    ImageAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var _this = this;
        var img = new Image();
        Tools.SetCorsBehavior(this.url, img);
        img.onload = function () {
            _this.image = img;
            onSuccess();
        };
        img.onerror = function (err) {
            onError("Error loading image", err);
        };
        img.src = this.url;
    };
    return ImageAssetTask;
}(AbstractAssetTask));
export { ImageAssetTask };
/**
 * Define a task used by AssetsManager to load 2D textures
 */
var TextureAssetTask = /** @class */ (function (_super) {
    __extends(TextureAssetTask, _super);
    /**
     * Creates a new TextureAssetTask object
     * @param name defines the name of the task
     * @param url defines the location of the file to load
     * @param noMipmap defines if mipmap should not be generated (default is false)
     * @param invertY defines if texture must be inverted on Y axis (default is true)
     * @param samplingMode defines the sampling mode to use (default is Texture.TRILINEAR_SAMPLINGMODE)
     */
    function TextureAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the file to load
     */
    url, 
    /**
     * Defines if mipmap should not be generated (default is false)
     */
    noMipmap, 
    /**
     * Defines if texture must be inverted on Y axis (default is true)
     */
    invertY, 
    /**
     * Defines the sampling mode to use (default is Texture.TRILINEAR_SAMPLINGMODE)
     */
    samplingMode) {
        if (invertY === void 0) { invertY = true; }
        if (samplingMode === void 0) { samplingMode = Texture.TRILINEAR_SAMPLINGMODE; }
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        _this.noMipmap = noMipmap;
        _this.invertY = invertY;
        _this.samplingMode = samplingMode;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    TextureAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var onload = function () {
            onSuccess();
        };
        var onerror = function (message, exception) {
            onError(message, exception);
        };
        this.texture = new Texture(this.url, scene, this.noMipmap, this.invertY, this.samplingMode, onload, onerror);
    };
    return TextureAssetTask;
}(AbstractAssetTask));
export { TextureAssetTask };
/**
 * Define a task used by AssetsManager to load cube textures
 */
var CubeTextureAssetTask = /** @class */ (function (_super) {
    __extends(CubeTextureAssetTask, _super);
    /**
     * Creates a new CubeTextureAssetTask
     * @param name defines the name of the task
     * @param url defines the location of the files to load (You have to specify the folder where the files are + filename with no extension)
     * @param extensions defines the extensions to use to load files (["_px", "_py", "_pz", "_nx", "_ny", "_nz"] by default)
     * @param noMipmap defines if mipmaps should not be generated (default is false)
     * @param files defines the explicit list of files (undefined by default)
     */
    function CubeTextureAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the files to load (You have to specify the folder where the files are + filename with no extension)
     */
    url, 
    /**
     * Defines the extensions to use to load files (["_px", "_py", "_pz", "_nx", "_ny", "_nz"] by default)
     */
    extensions, 
    /**
     * Defines if mipmaps should not be generated (default is false)
     */
    noMipmap, 
    /**
     * Defines the explicit list of files (undefined by default)
     */
    files) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        _this.extensions = extensions;
        _this.noMipmap = noMipmap;
        _this.files = files;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    CubeTextureAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var onload = function () {
            onSuccess();
        };
        var onerror = function (message, exception) {
            onError(message, exception);
        };
        this.texture = new CubeTexture(this.url, scene, this.extensions, this.noMipmap, this.files, onload, onerror);
    };
    return CubeTextureAssetTask;
}(AbstractAssetTask));
export { CubeTextureAssetTask };
/**
 * Define a task used by AssetsManager to load HDR cube textures
 */
var HDRCubeTextureAssetTask = /** @class */ (function (_super) {
    __extends(HDRCubeTextureAssetTask, _super);
    /**
     * Creates a new HDRCubeTextureAssetTask object
     * @param name defines the name of the task
     * @param url defines the location of the file to load
     * @param size defines the desired size (the more it increases the longer the generation will be) If the size is omitted this implies you are using a preprocessed cubemap.
     * @param noMipmap defines if mipmaps should not be generated (default is false)
     * @param generateHarmonics specifies whether you want to extract the polynomial harmonics during the generation process (default is true)
     * @param gammaSpace specifies if the texture will be use in gamma or linear space (the PBR material requires those texture in linear space, but the standard material would require them in Gamma space) (default is false)
     * @param reserved Internal use only
     */
    function HDRCubeTextureAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the file to load
     */
    url, 
    /**
     * Defines the desired size (the more it increases the longer the generation will be)
     */
    size, 
    /**
     * Defines if mipmaps should not be generated (default is false)
     */
    noMipmap, 
    /**
     * Specifies whether you want to extract the polynomial harmonics during the generation process (default is true)
     */
    generateHarmonics, 
    /**
     * Specifies if the texture will be use in gamma or linear space (the PBR material requires those texture in linear space, but the standard material would require them in Gamma space) (default is false)
     */
    gammaSpace, 
    /**
     * Internal Use Only
     */
    reserved) {
        if (noMipmap === void 0) { noMipmap = false; }
        if (generateHarmonics === void 0) { generateHarmonics = true; }
        if (gammaSpace === void 0) { gammaSpace = false; }
        if (reserved === void 0) { reserved = false; }
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        _this.size = size;
        _this.noMipmap = noMipmap;
        _this.generateHarmonics = generateHarmonics;
        _this.gammaSpace = gammaSpace;
        _this.reserved = reserved;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    HDRCubeTextureAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var onload = function () {
            onSuccess();
        };
        var onerror = function (message, exception) {
            onError(message, exception);
        };
        this.texture = new HDRCubeTexture(this.url, scene, this.size, this.noMipmap, this.generateHarmonics, this.gammaSpace, this.reserved, onload, onerror);
    };
    return HDRCubeTextureAssetTask;
}(AbstractAssetTask));
export { HDRCubeTextureAssetTask };
/**
 * Define a task used by AssetsManager to load Equirectangular cube textures
 */
var EquiRectangularCubeTextureAssetTask = /** @class */ (function (_super) {
    __extends(EquiRectangularCubeTextureAssetTask, _super);
    /**
     * Creates a new EquiRectangularCubeTextureAssetTask object
     * @param name defines the name of the task
     * @param url defines the location of the file to load
     * @param size defines the desired size (the more it increases the longer the generation will be)
     * If the size is omitted this implies you are using a preprocessed cubemap.
     * @param noMipmap defines if mipmaps should not be generated (default is false)
     * @param gammaSpace specifies if the texture will be used in gamma or linear space
     * (the PBR material requires those texture in linear space, but the standard material would require them in Gamma space)
     * (default is true)
     */
    function EquiRectangularCubeTextureAssetTask(
    /**
     * Defines the name of the task
     */
    name, 
    /**
     * Defines the location of the file to load
     */
    url, 
    /**
     * Defines the desired size (the more it increases the longer the generation will be)
     */
    size, 
    /**
     * Defines if mipmaps should not be generated (default is false)
     */
    noMipmap, 
    /**
     * Specifies if the texture will be use in gamma or linear space (the PBR material requires those texture in linear space,
     * but the standard material would require them in Gamma space) (default is true)
     */
    gammaSpace) {
        if (noMipmap === void 0) { noMipmap = false; }
        if (gammaSpace === void 0) { gammaSpace = true; }
        var _this = _super.call(this, name) || this;
        _this.name = name;
        _this.url = url;
        _this.size = size;
        _this.noMipmap = noMipmap;
        _this.gammaSpace = gammaSpace;
        return _this;
    }
    /**
     * Execute the current task
     * @param scene defines the scene where you want your assets to be loaded
     * @param onSuccess is a callback called when the task is successfully executed
     * @param onError is a callback called if an error occurs
     */
    EquiRectangularCubeTextureAssetTask.prototype.runTask = function (scene, onSuccess, onError) {
        var onload = function () {
            onSuccess();
        };
        var onerror = function (message, exception) {
            onError(message, exception);
        };
        this.texture = new EquiRectangularCubeTexture(this.url, scene, this.size, this.noMipmap, this.gammaSpace, onload, onerror);
    };
    return EquiRectangularCubeTextureAssetTask;
}(AbstractAssetTask));
export { EquiRectangularCubeTextureAssetTask };
/**
 * This class can be used to easily import assets into a scene
 * @see https://doc.babylonjs.com/how_to/how_to_use_assetsmanager
 */
var AssetsManager = /** @class */ (function () {
    /**
     * Creates a new AssetsManager
     * @param scene defines the scene to work on
     */
    function AssetsManager(scene) {
        this._isLoading = false;
        this._tasks = new Array();
        this._waitingTasksCount = 0;
        this._totalTasksCount = 0;
        /**
         * Observable called when all tasks are processed
         */
        this.onTaskSuccessObservable = new Observable();
        /**
         * Observable called when a task had an error
         */
        this.onTaskErrorObservable = new Observable();
        /**
         * Observable called when all tasks were executed
         */
        this.onTasksDoneObservable = new Observable();
        /**
         * Observable called when a task is done (whatever the result is)
         */
        this.onProgressObservable = new Observable();
        /**
         * Gets or sets a boolean defining if the AssetsManager should use the default loading screen
         * @see https://doc.babylonjs.com/how_to/creating_a_custom_loading_screen
         */
        this.useDefaultLoadingScreen = true;
        /**
         * Gets or sets a boolean defining if the AssetsManager should automatically hide the loading screen
         * when all assets have been downloaded.
         * If set to false, you need to manually call in hideLoadingUI() once your scene is ready.
         */
        this.autoHideLoadingUI = true;
        this._scene = scene;
    }
    /**
     * Add a ContainerAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param meshesNames defines the name of meshes to load
     * @param rootUrl defines the root url to use to locate files
     * @param sceneFilename defines the filename of the scene file
     * @returns a new ContainerAssetTask object
     */
    AssetsManager.prototype.addContainerTask = function (taskName, meshesNames, rootUrl, sceneFilename) {
        var task = new ContainerAssetTask(taskName, meshesNames, rootUrl, sceneFilename);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a MeshAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param meshesNames defines the name of meshes to load
     * @param rootUrl defines the root url to use to locate files
     * @param sceneFilename defines the filename of the scene file
     * @returns a new MeshAssetTask object
     */
    AssetsManager.prototype.addMeshTask = function (taskName, meshesNames, rootUrl, sceneFilename) {
        var task = new MeshAssetTask(taskName, meshesNames, rootUrl, sceneFilename);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a TextFileAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @returns a new TextFileAssetTask object
     */
    AssetsManager.prototype.addTextFileTask = function (taskName, url) {
        var task = new TextFileAssetTask(taskName, url);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a BinaryFileAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @returns a new BinaryFileAssetTask object
     */
    AssetsManager.prototype.addBinaryFileTask = function (taskName, url) {
        var task = new BinaryFileAssetTask(taskName, url);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a ImageAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @returns a new ImageAssetTask object
     */
    AssetsManager.prototype.addImageTask = function (taskName, url) {
        var task = new ImageAssetTask(taskName, url);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a TextureAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @param noMipmap defines if the texture must not receive mipmaps (false by default)
     * @param invertY defines if you want to invert Y axis of the loaded texture (false by default)
     * @param samplingMode defines the sampling mode to use (Texture.TRILINEAR_SAMPLINGMODE by default)
     * @returns a new TextureAssetTask object
     */
    AssetsManager.prototype.addTextureTask = function (taskName, url, noMipmap, invertY, samplingMode) {
        if (samplingMode === void 0) { samplingMode = Texture.TRILINEAR_SAMPLINGMODE; }
        var task = new TextureAssetTask(taskName, url, noMipmap, invertY, samplingMode);
        this._tasks.push(task);
        return task;
    };
    /**
     * Add a CubeTextureAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @param extensions defines the extension to use to load the cube map (can be null)
     * @param noMipmap defines if the texture must not receive mipmaps (false by default)
     * @param files defines the list of files to load (can be null)
     * @returns a new CubeTextureAssetTask object
     */
    AssetsManager.prototype.addCubeTextureTask = function (taskName, url, extensions, noMipmap, files) {
        var task = new CubeTextureAssetTask(taskName, url, extensions, noMipmap, files);
        this._tasks.push(task);
        return task;
    };
    /**
     *
     * Add a HDRCubeTextureAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @param size defines the size you want for the cubemap (can be null)
     * @param noMipmap defines if the texture must not receive mipmaps (false by default)
     * @param generateHarmonics defines if you want to automatically generate (true by default)
     * @param gammaSpace specifies if the texture will be use in gamma or linear space (the PBR material requires those texture in linear space, but the standard material would require them in Gamma space) (default is false)
     * @param reserved Internal use only
     * @returns a new HDRCubeTextureAssetTask object
     */
    AssetsManager.prototype.addHDRCubeTextureTask = function (taskName, url, size, noMipmap, generateHarmonics, gammaSpace, reserved) {
        if (noMipmap === void 0) { noMipmap = false; }
        if (generateHarmonics === void 0) { generateHarmonics = true; }
        if (gammaSpace === void 0) { gammaSpace = false; }
        if (reserved === void 0) { reserved = false; }
        var task = new HDRCubeTextureAssetTask(taskName, url, size, noMipmap, generateHarmonics, gammaSpace, reserved);
        this._tasks.push(task);
        return task;
    };
    /**
     *
     * Add a EquiRectangularCubeTextureAssetTask to the list of active tasks
     * @param taskName defines the name of the new task
     * @param url defines the url of the file to load
     * @param size defines the size you want for the cubemap (can be null)
     * @param noMipmap defines if the texture must not receive mipmaps (false by default)
     * @param gammaSpace Specifies if the texture will be used in gamma or linear space
     * (the PBR material requires those textures in linear space, but the standard material would require them in Gamma space)
     * @returns a new EquiRectangularCubeTextureAssetTask object
     */
    AssetsManager.prototype.addEquiRectangularCubeTextureAssetTask = function (taskName, url, size, noMipmap, gammaSpace) {
        if (noMipmap === void 0) { noMipmap = false; }
        if (gammaSpace === void 0) { gammaSpace = true; }
        var task = new EquiRectangularCubeTextureAssetTask(taskName, url, size, noMipmap, gammaSpace);
        this._tasks.push(task);
        return task;
    };
    /**
     * Remove a task from the assets manager.
     * @param task the task to remove
     */
    AssetsManager.prototype.removeTask = function (task) {
        var index = this._tasks.indexOf(task);
        if (index > -1) {
            this._tasks.splice(index, 1);
        }
    };
    AssetsManager.prototype._decreaseWaitingTasksCount = function (task) {
        this._waitingTasksCount--;
        try {
            if (this.onProgress) {
                this.onProgress(this._waitingTasksCount, this._totalTasksCount, task);
            }
            this.onProgressObservable.notifyObservers(new AssetsProgressEvent(this._waitingTasksCount, this._totalTasksCount, task));
        }
        catch (e) {
            Logger.Error("Error running progress callbacks.");
            console.log(e);
        }
        if (this._waitingTasksCount === 0) {
            try {
                var currentTasks = this._tasks.slice();
                if (this.onFinish) {
                    // Calling onFinish with immutable array of tasks
                    this.onFinish(currentTasks);
                }
                // Let's remove successfull tasks
                for (var _i = 0, currentTasks_1 = currentTasks; _i < currentTasks_1.length; _i++) {
                    var task = currentTasks_1[_i];
                    if (task.taskState === AssetTaskState.DONE) {
                        var index = this._tasks.indexOf(task);
                        if (index > -1) {
                            this._tasks.splice(index, 1);
                        }
                    }
                }
                this.onTasksDoneObservable.notifyObservers(this._tasks);
            }
            catch (e) {
                Logger.Error("Error running tasks-done callbacks.");
                console.log(e);
            }
            this._isLoading = false;
            if (this.autoHideLoadingUI) {
                this._scene.getEngine().hideLoadingUI();
            }
        }
    };
    AssetsManager.prototype._runTask = function (task) {
        var _this = this;
        var done = function () {
            try {
                if (_this.onTaskSuccess) {
                    _this.onTaskSuccess(task);
                }
                _this.onTaskSuccessObservable.notifyObservers(task);
                _this._decreaseWaitingTasksCount(task);
            }
            catch (e) {
                error("Error executing task success callbacks", e);
            }
        };
        var error = function (message, exception) {
            task._setErrorObject(message, exception);
            if (_this.onTaskError) {
                _this.onTaskError(task);
            }
            _this.onTaskErrorObservable.notifyObservers(task);
            _this._decreaseWaitingTasksCount(task);
        };
        task.run(this._scene, done, error);
    };
    /**
     * Reset the AssetsManager and remove all tasks
     * @return the current instance of the AssetsManager
     */
    AssetsManager.prototype.reset = function () {
        this._isLoading = false;
        this._tasks = new Array();
        return this;
    };
    /**
     * Start the loading process
     * @return the current instance of the AssetsManager
     */
    AssetsManager.prototype.load = function () {
        if (this._isLoading) {
            return this;
        }
        this._isLoading = true;
        this._waitingTasksCount = this._tasks.length;
        this._totalTasksCount = this._tasks.length;
        if (this._waitingTasksCount === 0) {
            this._isLoading = false;
            if (this.onFinish) {
                this.onFinish(this._tasks);
            }
            this.onTasksDoneObservable.notifyObservers(this._tasks);
            return this;
        }
        if (this.useDefaultLoadingScreen) {
            this._scene.getEngine().displayLoadingUI();
        }
        for (var index = 0; index < this._tasks.length; index++) {
            var task = this._tasks[index];
            if (task.taskState === AssetTaskState.INIT) {
                this._runTask(task);
            }
        }
        return this;
    };
    /**
     * Start the loading process as an async operation
     * @return a promise returning the list of failed tasks
     */
    AssetsManager.prototype.loadAsync = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            if (_this._isLoading) {
                resolve();
                return;
            }
            _this.onTasksDoneObservable.addOnce(function (remainingTasks) {
                if (remainingTasks && remainingTasks.length) {
                    reject(remainingTasks);
                }
                else {
                    resolve();
                }
            });
            _this.load();
        });
    };
    return AssetsManager;
}());
export { AssetsManager };
//# sourceMappingURL=assetsManager.js.map